import { useMutation } from "@apollo/client";
import type { UserSettingsShape } from "components/settings/UserSettingsSchema";
import { UserSettingsSchema } from "components/settings/UserSettingsSchema";
import { userSettingsAtom } from "components/settings/atoms";
import { useAtom } from "jotai";
import ChangeUserSettingMutation from "lib/graphql/mutations/settings/ChangeUserSettingMutation";
import type {
  IChangeUserSettingMutation,
  IChangeUserSettingMutationVariables,
} from "lib/graphql/mutations/settings/__generated__/ChangeUserSettingMutation.generated";
import useSelfie from "lib/hooks/useSelfie";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import has from "lodash/has";
import set from "lodash/set";
import { startTransition } from "react";
import type { Get, Paths } from "type-fest";
import { useDebounceCallback } from "usehooks-ts";

export function useSpecificSetting<
  Key extends Paths<UserSettingsShape>,
  ValueForPath extends Get<UserSettingsShape, Key>,
  DefaultValueForPath extends Get<UserSettingsShape, Key>,
  ValueFn extends (currentSettingValue: Get<UserSettingsShape, Key>) => Get<UserSettingsShape, Key>,
>(key: Key, defaultNotSet: DefaultValueForPath | null = null) {
  const [localSettings, setSettings] = useAtom(userSettingsAtom);
  const viewer = useSelfie();

  const settingsFromViewer = UserSettingsSchema.partial().parse(viewer?.settingsV2 || {});
  const currentValue = get(settingsFromViewer, key) as ValueForPath | undefined;

  const [mutate, { loading }] = useMutation<IChangeUserSettingMutation, IChangeUserSettingMutationVariables>(
    ChangeUserSettingMutation,
  );
  const mutateWithDebounce = useDebounceCallback(mutate, 300);
  const hasLocalSettingCopy = has(localSettings, key);

  const setSettting = async (valueOrValueProducerFn: ValueFn | ValueForPath) => {
    if (viewer) {
      const existingSettings = UserSettingsSchema.parse(cloneDeep(viewer.settingsV2));
      const valueToSubmit =
        typeof valueOrValueProducerFn === "function"
          ? valueOrValueProducerFn(get(settingsFromViewer, key) as ValueForPath)
          : valueOrValueProducerFn;
      const valueToSet =
        typeof valueOrValueProducerFn === "function"
          ? valueOrValueProducerFn(get(localSettings, key) as ValueForPath)
          : valueOrValueProducerFn;
      setSettings(set(existingSettings, key, valueToSet));
      startTransition(() => {
        mutateWithDebounce({
          variables: {
            key,
            value: valueToSubmit,
          },
        });
      });
      return null;
    }

    const valueToSet =
      typeof valueOrValueProducerFn === "function"
        ? valueOrValueProducerFn(get(localSettings, key) as ValueForPath)
        : valueOrValueProducerFn;
    const modifiedSettings = set(localSettings, key, valueToSet);
    setSettings(modifiedSettings);
    return null;
  };

  if (hasLocalSettingCopy) {
    return {
      value: get(localSettings, key) as ValueForPath,
      set: setSettting,
      loading,
    };
  }
  if (viewer) {
    if (currentValue === undefined) {
      return {
        value: defaultNotSet as DefaultValueForPath,
        set: setSettting,
        loading,
      };
    }
    return {
      // eslint-disable-next-line no-nested-ternary
      value: currentValue as ValueForPath,
      set: setSettting,
      loading,
    };
  }
  return {
    value: defaultNotSet as DefaultValueForPath,
    set: setSettting,
    loading,
  };
}
